import { useState } from "react";
import { Navigate } from "react-router-dom";
import {
  MiBox,
  MiBuildPageHeader,
  MiButton,
  MiList,
  MiMenu,
  MiTab,
} from "@miplatform/components";
import {
  BUTTON_LABELS,
  BUTTON_VARIANTS,
  PAGINATION,
} from "@miplatform/constants";
import { useRouter } from "@miplatform/hooks";
import { mdiPencilOutline, mdiPlus } from "@mdi/js";
import { ErrorPage } from "@mibuildweb/components";
import { Box } from "@mui/material";
import { usePermissions } from "../../hooks";
import { AddAccount } from "./AddAccount/AddAccount";
import {
  ACCOUNTS_TAB_TO_PERMISSIONS,
  ACCOUNTS_TAB_TO_TYPE,
  ACCOUNT_PAGE_CONFIG,
} from "./accountsConstants";
import { useAccountsViewModel } from "./useAccountsViewModel";

export const Accounts = ({ type }) => {
  const [showDrawer, setShowDrawer] = useState(false);

  const {
    navigate,
    params: { tab },
  } = useRouter();
  const { hasPermission } = usePermissions();

  const {
    isLoading,
    refresh,
    accounts,
    search,
    debouncedSearch,
    setSearch,
    handleRefresh,
    setAccounts,
    getAccounts,
    getRecentAccounts,
    setRecentAccounts,
    recentAccounts,
  } = useAccountsViewModel({ tab });

  const tabs = ACCOUNT_PAGE_CONFIG[type].tabs.filter((tabItem) =>
    hasPermission(ACCOUNTS_TAB_TO_PERMISSIONS[tabItem.value]?.READ)
  );

  const sections = [
    {
      fieldset: [
        {
          label: "Name",
          field: "name",
        },
        {
          label: "Abbreviation",
          field: "code",
        },
        {
          label: "Status",
          field: "status",
        },
        {
          label: "Address",
          field: "address",
          renderValue: ({ address }) =>
            `${address.addressLine1}, ${address.city}, ${address.regionCode} ${address.postalCode}`,
        },
      ],
    },
  ];

  const handleNavigate = (item, readOnly) => {
    navigate(
      `/accounting/${ACCOUNT_PAGE_CONFIG[type].path}/${tab}/${item.id}/summary`,
      {
        state: { readOnly },
      }
    );
  };

  const renderItemActions = (item) =>
    hasPermission(ACCOUNTS_TAB_TO_PERMISSIONS[tab]?.UPDATE) && (
      <Box onClick={(e) => e.stopPropagation()}>
        <MiMenu
          data={[
            {
              label: BUTTON_LABELS.EDIT,
              icon: mdiPencilOutline,
              action: () => handleNavigate(item, false),
            },
          ]}
        />
      </Box>
    );

  const renderActions = () =>
    hasPermission(ACCOUNTS_TAB_TO_PERMISSIONS[tab]?.CREATE) && (
      <MiButton
        title={ACCOUNTS_TAB_TO_TYPE[tab]}
        variant={BUTTON_VARIANTS.CONTAINED}
        startIcon={{ path: mdiPlus }}
        onClick={() => setShowDrawer(true)}
      />
    );

  const handleTabChange = (tab) => {
    setSearch("");
    handleRefresh();
    navigate(`/accounting/${ACCOUNT_PAGE_CONFIG[type].path}/${tab}`);
  };

  const handleCardClick = (item) => {
    handleNavigate(item, true);
  };

  if (!hasPermission(ACCOUNTS_TAB_TO_PERMISSIONS[tab]?.READ)) {
    const allowedTab = ACCOUNT_PAGE_CONFIG[type].tabs.find((tabItem) =>
      hasPermission(ACCOUNTS_TAB_TO_PERMISSIONS[tabItem.value]?.READ)
    );

    if (allowedTab) {
      return (
        <Navigate
          replace
          to={`/accounting/${ACCOUNT_PAGE_CONFIG[type].path}/${allowedTab.value}`}
        />
      );
    }

    return <ErrorPage />;
  }

  return (
    <>
      <MiBuildPageHeader
        title={ACCOUNT_PAGE_CONFIG[type].plural}
        loading={isLoading}
        search={search}
        onHandleSearch={(e) => setSearch(e.target.value)}
        actionComponent={renderActions()}
      />
      {tabs.length > 0 && (
        <MiTab tabs={tabs} currentTab={tab} onTabChange={handleTabChange} />
      )}
      <MiBox>
        <MiList
          viewType="card"
          showRecent
          cardProps={{
            sections,
            renderItemActions,
            onClick: handleCardClick,
            leftIconProps: { fieldset: [] },
          }}
          search={debouncedSearch}
          loading={isLoading}
          data={accounts}
          setData={setAccounts}
          dataProvider={getAccounts}
          recentData={recentAccounts}
          setRecentData={setRecentAccounts}
          recentDataProvider={getRecentAccounts}
          refresh={refresh}
          paginationMode={PAGINATION.SERVER}
          emptyListProps={{
            text: "No Customers found",
          }}
          data-testid="accounts-list"
        />
      </MiBox>
      {showDrawer && (
        <AddAccount
          type={type}
          tab={tab}
          showDrawer={showDrawer}
          onCloseDrawer={() => setShowDrawer(false)}
          onRefresh={handleRefresh}
        />
      )}
    </>
  );
};
